import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AmbassadorShell, { card, eyebrow, h1Style, ink, muted, gold } from "./_AmbassadorShell";

export default function AmbassadorTerms() {
  return (
    <AmbassadorShell>
      <Helmet>
        <title>Ambassador Program Terms | Eva Insiders</title>
        <meta name="description" content="The terms of the Eva Insiders Ambassador Program: commission rates, attribution windows, payout rules and the small print." />
        <link rel="canonical" href="https://hireeva.co/ambassadors/terms" />
        <meta property="og:title" content="Ambassador Program Terms — Eva Insiders" />
        <meta property="og:description" content="Commission rates, attribution and payout rules for the Eva Insiders Ambassador Program." />
        <meta property="og:url" content="https://hireeva.co/ambassadors/terms" />
        <meta property="og:type" content="article" />
      </Helmet>
      <p style={eyebrow}>
        <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
        Updated October 1, 2026
      </p>
      <h1 style={h1Style}>Ambassador Program <em>Terms.</em></h1>
      <div style={{ ...card, background: "#FBF8F1", borderColor: gold, marginBottom: 24 }}>
        <p style={{ margin: 0, fontStyle: "italic", color: ink, fontSize: 16, lineHeight: 1.6 }}>
          This is the boring part. Read it once, then come build. Nothing here is a trap — I just want us aligned before money starts moving.
        </p>
        <p style={{ margin: "10px 0 0", color: muted, fontSize: 14, lineHeight: 1.6 }}>
          The Program is about the room you build, not just the commissions. If you haven't yet, read the <Link to="/ambassadors/playbook" style={{ color: ink, textDecoration: "underline", textUnderlineOffset: 2 }}>Circle playbook</Link> — it's how this actually works in practice.
        </p>
      </div>
      <div style={{ display: "grid", gap: 18, fontSize: 16, color: "#3a3530" }}>
        <Block n="1." h="What this is">
          The Eva Insiders Ambassador Program ("Program") lets you ("Ambassador") refer paying customers to ElectricSheep ("we", "us") and earn cash commissions. By applying you agree to these terms.
        </Block>
        <Block n="2." h="Commission rates">
          <ul style={ulStyle}>
            <li><strong>10% of the subscription fees</strong> the referred customer pays for its first 3 billing months, net of discounts and credits and excluding taxes. After that, no further commission is paid on that customer.</li>
            <li>Purchases billed separately from the subscription (such as projects, extra capacity packs, audits, training and on-site days) don't count.</li>
            <li>If the customer pays its first 3 months up front, the commission is 10% of that payment, paid once.</li>
            <li>Commission is paid only on customers you refer directly. There is no second-tier or override commission on customers referred by anyone else.</li>
          </ul>
          <table style={tableStyle}>
            <thead>
              <tr><th style={thStyle}>Plan</th><th style={{ ...thStyle, textAlign: "right" }}>Commission</th></tr>
            </thead>
            <tbody>
              {RATE_EXAMPLES.map(([plan, amount]) => (
                <tr key={plan}><td style={tdStyle}>{plan}</td><td style={{ ...tdStyle, textAlign: "right" }}>{amount}</td></tr>
              ))}
            </tbody>
          </table>
        </Block>
        <Block n="3." h="Who counts as a referral">
          The customer must be a business with no current or previous paid ElectricSheepHQ or HireEva account, and must buy through your referral link or code. The rules in section 6 apply to every referral. We may decline or reverse commissions that don't meet these rules.
        </Block>
        <Block n="4." h="Approval window & payout">
          Each commission is <em>pending</em> until the invoice is paid and 30 days pass with no refund, chargeback or payment dispute. It then becomes <em>approved</em>. Approved commissions are paid on the 1st of each month via Stripe Connect Express directly to your bank account. Minimum payout is $50; smaller balances roll forward to the next month.
        </Block>
        <Block n="5." h="Refunds, chargebacks, disputes">
          A refund, chargeback or payment dispute on a commissioned payment reverses the matching commission. If your balance goes negative, the deficit rolls forward to your next payout. We never invoice you for a negative balance.
        </Block>
        <Block n="6." h="What's not allowed">
          <ul style={ulStyle}>
            <li>Self-referrals (the buyer's email may not match your account email or any account you control).</li>
            <li>Paid search bidding on our brand terms ("Eva", "ElectricSheep", "Hire Eva", etc.).</li>
            <li>Spam, fake reviews, fake testimonials, or any deceptive marketing.</li>
            <li>Sharing your code in coupon-aggregator sites without our written permission.</li>
            <li>Implying you work for or represent ElectricSheep beyond being an Ambassador.</li>
          </ul>
          We may suspend or ban an ambassador and zero out pending commissions for any violation, fraud, or abuse.
        </Block>
        <Block n="7." h="Circle perks · founder drop-ins">
          Tier milestones unlock recurring founder drop-ins to the Circle you're already running with your Inner Circle:
          <ul style={ulStyle}>
            <li><strong>Tier 1 (5+ active referrals):</strong> founder drops into your Circle once a month for 30 minutes.</li>
            <li><strong>Tier 2 (25+ active referrals):</strong> founder drops into your Circle every week for a full hour.</li>
          </ul>
          An active referral is a referred customer with an active paid plan. You host the room. The founder shows up to it. Founder time is offered subject to availability. A minimum of 5 RSVP confirmations is required for the founder to attend a scheduled Circle session — below that, you keep the room on your own.
        </Block>
        <Block n="8." h="Tax">
          You're responsible for taxes on your commissions. We collect a W-9 (US) or W-8BEN (outside the US) before your first payout, and issue Form 1099 when US reporting rules require it.
        </Block>
        <Block n="9." h="Stripe Connect">
          Payouts are processed by Stripe Inc. via Connect Express accounts. You must complete Stripe's identity and banking verification before receiving your first payout. Your use of payouts is also governed by the <a href="https://stripe.com/connect-account/legal/full" style={{ color: ink }}>Stripe Connected Account Agreement</a> and <a href="https://stripe.com/legal/ssa" style={{ color: ink }}>Stripe Services Agreement</a>.
        </Block>
        <Block n="10." h="Changes & termination">
          We may change rates or terms with 30 days' notice. Customers referred before a change keep the rate in effect when they were referred, for the remainder of their commission window. We may end the Program at any time; approved commissions are paid in the final monthly run.
        </Block>
        <Block n="11." h="Founding Insiders bonus (limited)">
          During the program's launch period, additional rewards are awarded to early Insiders, separate from the standing tiers above:
          <ul style={ulStyle}>
            <li><strong>First 5 Insiders to reach 5 referrals:</strong> a weekly 30-minute founder drop-in to your Circle for 3 consecutive months.</li>
            <li><strong>First Insider to reach 10 referrals:</strong> a weekly 1-hour founder drop-in to your Circle for 3 consecutive months.</li>
          </ul>
          Tracking begins on program launch. Winners are confirmed in their dashboard the day they qualify. Bonus calls are in addition to — not a replacement for — standing tier perks.
        </Block>
      </div>
    </AmbassadorShell>
  );
}

function Block({ n, h, children }: { n: string; h: string; children: React.ReactNode }) {
  return (
    <div style={card}>
      <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
        <span style={{ ...eyebrow, color: muted, marginBottom: 0 }}>{n}</span>
        <h2 style={{ fontSize: 20, margin: 0, fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400, color: ink }}>{h}</h2>
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

const ulStyle: React.CSSProperties = { paddingLeft: 20, margin: 0, display: "grid", gap: 6 };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", marginTop: 14, fontSize: 15 };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid rgba(0,0,0,0.12)", color: muted, fontWeight: 600, fontSize: 13, letterSpacing: "0.02em" };
const tdStyle: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid rgba(0,0,0,0.06)" };

const RATE_EXAMPLES: [string, string][] = [
  ["Maintain Velocity ($695/mo)", "$208.50"],
  ["Build Velocity 1 ($1,500/mo)", "$450"],
  ["Build Velocity 2 ($3,000/mo)", "$900"],
  ["Build Velocity 3 ($5,000/mo)", "$1,500"],
  ["Build Velocity 1, first 3 months paid up front ($4,275)", "$427.50"],
];
